import React from 'react';
import { useHistory } from 'react-router-dom';
import {
  CommandDialog,
  CommandList,
  CommandEmpty,
  Command,
} from '@dashboard/components/ui/command';
import { Sheet, SheetContent } from '@dashboard/components/ui/sheet';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { useCommandPalette } from './CommandPaletteContext';
import { useCommandPaletteHotkey } from './useCommandPaletteHotkey';
import { useIsMobile } from './useIsMobile';
import { PaletteInput } from './PaletteInput';
import { PaletteStaticGroup } from './PaletteStaticGroup';
import { PaletteDataGroup } from './PaletteDataGroup';
import { PaletteEmptyState } from './PaletteEmptyState';
import { navigationEntries } from './registries/navigation';
import { settingsEntries } from './registries/settings';
import { actionEntries } from './registries/actions';
import {
  useCheckoutPagesSearch,
  useCustomersSearch,
  usePaymentsSearch,
  useSubscriptionsSearch,
  useBookingsSearch,
  useSubmissionsSearch,
  useEventsSearch,
  useFormsSearch,
  useCouponsSearch,
} from './groups/hooks';
import { useDocsSearch } from './groups/useDocsSearch';

export function CommandPalette() {
  const { open, query, closePalette, pageEntries } = useCommandPalette();
  useCommandPaletteHotkey();
  const history = useHistory();
  const { logout } = useAuth();
  const isMobile = useIsMobile();

  const children = (
    <>
      <PaletteInput />
      <CommandList>
        {query.length === 0 ? (
          <PaletteEmptyState />
        ) : (
          <>
            <CommandEmpty>
              No results for &quot;{query}&quot;. Try a customer email, payment amount, or a page
              name.
            </CommandEmpty>
            <PaletteStaticGroup
              heading="This page"
              groupKey="this_page"
              entries={pageEntries}
              query={query}
            />
            <PaletteDataGroup
              heading="Checkout pages"
              groupKey="checkout_pages"
              query={query}
              useSearch={useCheckoutPagesSearch}
            />
            <PaletteDataGroup
              heading="Events"
              groupKey="events"
              query={query}
              useSearch={useEventsSearch}
            />
            <PaletteDataGroup
              heading="Forms"
              groupKey="forms"
              query={query}
              useSearch={useFormsSearch}
            />
            <PaletteDataGroup
              heading="Payments"
              groupKey="payments"
              query={query}
              useSearch={usePaymentsSearch}
            />
            <PaletteDataGroup
              heading="Subscriptions"
              groupKey="subscriptions"
              query={query}
              useSearch={useSubscriptionsSearch}
            />
            <PaletteDataGroup
              heading="Bookings"
              groupKey="bookings"
              query={query}
              useSearch={useBookingsSearch}
            />
            <PaletteDataGroup
              heading="Submissions"
              groupKey="submissions"
              query={query}
              useSearch={useSubmissionsSearch}
            />
            <PaletteDataGroup
              heading="Customers"
              groupKey="customers"
              query={query}
              useSearch={useCustomersSearch}
            />
            <PaletteDataGroup
              heading="Coupons"
              groupKey="coupons"
              query={query}
              useSearch={useCouponsSearch}
            />
            <PaletteStaticGroup
              heading="Navigation"
              groupKey="navigation"
              entries={navigationEntries}
              query={query}
            />
            <PaletteStaticGroup
              heading="Settings"
              groupKey="settings"
              entries={settingsEntries}
              query={query}
            />
            <PaletteStaticGroup
              heading="Actions"
              groupKey="actions"
              entries={actionEntries}
              getCtx={() => ({ history, logout })}
              query={query}
            />
            <PaletteDataGroup
              heading="Knowledge base & docs"
              groupKey="docs"
              query={query}
              useSearch={useDocsSearch}
              openInNewTab
            />
          </>
        )}
      </CommandList>
    </>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={next => (next ? null : closePalette())}>
        <SheetContent side="bottom" className="h-[90vh] p-0">
          <Command shouldFilter={false}>{children}</Command>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <CommandDialog
      open={open}
      onOpenChange={next => (next ? null : closePalette())}
      shouldFilter={false}
    >
      {children}
    </CommandDialog>
  );
}
